"use client";

import React, { useState } from "react";
import { X, Upload, Bug, Lightbulb } from "lucide-react";
import { FeedbackService } from "../../service/feedbackService";

interface FeedbackPopupProps {
  onClose: () => void;
}

const FeedbackPopup: React.FC<FeedbackPopupProps> = ({ onClose }) => {
  const [feedbackType, setFeedbackType] = useState<"bug" | "improvement">("bug");
  const [description, setDescription] = useState("");
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Pre-populated prompts based on feedback type
  const getPrompt = (type: "bug" | "improvement") => {
    if (type === "bug") {
      return "Please describe the bug you encountered:\n\n1. What were you trying to do?\n2. What happened instead?\n3. What browser are you using?\n4. Any additional details that might help us reproduce the issue?";
    } else {
      return "Please describe your improvement suggestion:\n\n1. What feature or area would you like to see improved?\n2. How do you think it could be better?\n3. What would this improvement help you achieve?\n4. Any additional thoughts or ideas?";
    }
  };

  // Update description when feedback type changes
  React.useEffect(() => {
    if (!description || description === getPrompt("bug") || description === getPrompt("improvement")) {
      setDescription(getPrompt(feedbackType));
    }
  }, [feedbackType]);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith("image/")) {
      setScreenshot(file);
    } else {
      alert("Please select a valid image file");
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsSubmitting(true);

    try {
      await FeedbackService.submitFeedbackWithScreenshot(
        {
          type: feedbackType,
          description: description,
          url: window.location.href,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
        },
        screenshot || undefined
      );

      setIsSubmitted(true);
      setTimeout(() => {
        onClose();
      }, 2000);

    } catch (error) {
      console.error("Error submitting feedback:", error);
      alert("There was an error submitting your feedback. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
        <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-green-600 text-2xl">✓</span>
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Thank You!</h3>
          <p className="text-gray-600">Your feedback has been sent to Sandy. We appreciate your help in improving WiderTalk!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900">Send Feedback</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Feedback Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              What type of feedback would you like to share?
            </label>
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => setFeedbackType("bug")}
                className={`flex-1 p-4 rounded-lg border-2 transition-colors ${
                  feedbackType === "bug"
                    ? "border-red-500 bg-red-50 text-red-700"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <Bug className="w-6 h-6 mx-auto mb-2" />
                <div className="font-medium">Bug Report</div>
                <div className="text-sm text-gray-500">Something isn't working</div>
              </button>

              <button
                type="button"
                onClick={() => setFeedbackType("improvement")}
                className={`flex-1 p-4 rounded-lg border-2 transition-colors ${
                  feedbackType === "improvement"
                    ? "border-blue-500 bg-blue-50 text-blue-700"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <Lightbulb className="w-6 h-6 mx-auto mb-2" />
                <div className="font-medium">Improvement</div>
                <div className="text-sm text-gray-500">Suggest an enhancement</div>
              </button>
            </div>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={8}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-widertalk-navy focus:border-transparent resize-none"
              required
            />
          </div>

          {/* Screenshot Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Screenshot (optional)
            </label>
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                <Upload size={16} />
                <span className="text-sm">Choose file</span>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                />
              </label>
              {screenshot && (
                <span className="text-sm text-gray-600">{screenshot.name}</span>
              )}
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !description.trim()}
              className="flex-1 px-4 py-2 bg-widertalk-navy text-white rounded-lg hover:bg-widertalk-navy/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? "Sending..." : "Send Feedback"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FeedbackPopup;
